import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useRegion } from '../hooks/useRegion';
import { Layout } from '../components/Layout';
import { PhoneTitle } from '../components/PhoneHeader';
import { PhoneList, PhoneListRow } from '../components/PhoneList';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { DatePicker } from '../components/ui/date-picker';
import { CategoryBadge, signPrefix } from '../components/equipment';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { formatDate } from '../lib/utils';
import { Loader2, TrendingUp, TrendingDown, DollarSign, Calendar, Plus, Trash2, ExternalLink, Save, Edit, Upload, Download } from 'lucide-react';
import { toast } from 'sonner';
import { BackButton } from '../components/BackButton';
import FileDropzone from '@/components/FileDropzone';

const CATEGORIES = [
  'Camera',
  'Lenses',
  'Lighting',
  'Grip',
  'Audio',
  'Monitors',
  'Accessories',
  'Other'
];

/** An income entry's length: "1 day", "2 days", "1 week". */
function entryTime(entry) {
  const n = Number(entry.days) || 0;
  const unit = entry.rate_mode === 'week' ? 'week' : 'day';
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

export default function EquipmentDetailPage() {
  const { equipmentId } = useParams();
  const navigate = useNavigate();
  const { fetchEquipmentById, updateEquipment, addHistoricalIncome, removeHistoricalIncome, updateHistoricalIncome, bulkImportHistoricalIncome } = useAppStore();
  const { formatCurrency, regionConfig } = useRegion();
  
  const [equipment, setEquipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showHistoricalDialog, setShowHistoricalDialog] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [deleteEntryId, setDeleteEntryId] = useState(null);
  
  // CSV Import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    serial_numbers: '',
    category: 'Camera',
    purchase_price: '',
    purchase_date: '',
    daily_rate: '',
    weekly_rate: '',
    is_active: true,
    // Disposal fields
    sale_price: '',
    disposal_date: '',
    buyer_notes: ''
  });
  
  const [historicalForm, setHistoricalForm] = useState({
    description: '',
    date: new Date().toISOString().split('T')[0],
    days: '',
    income: ''
  });

  useEffect(() => {
    loadEquipment();
  }, [equipmentId]);

  const loadEquipment = async () => {
    setLoading(true);
    try {
      const data = await fetchEquipmentById(equipmentId);
      setEquipment(data);
      setFormData({
        name: data.name || '',
        description: data.description || '',
        serial_numbers: data.serial_numbers || '',
        category: data.category || 'Camera',
        purchase_price: data.purchase_price || '',
        purchase_date: data.purchase_date || '',
        daily_rate: data.daily_rate || '',
        weekly_rate: data.weekly_rate || '',
        is_active: data.is_active !== false,
        // Disposal fields
        sale_price: data.sale_price || '',
        disposal_date: data.disposal_date || '',
        buyer_notes: data.buyer_notes || ''
      });
    } catch (error) {
      toast.error('Failed to load equipment');
      navigate('/equipment');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        ...formData,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        daily_rate: parseFloat(formData.daily_rate) || 0,
        weekly_rate: formData.weekly_rate !== '' ? parseFloat(formData.weekly_rate) : null,
        sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null,
        disposal_date: formData.disposal_date || null,
        buyer_notes: formData.buyer_notes || null
      };
      await updateEquipment(equipmentId, updateData);
      await loadEquipment();
      setIsEditing(false);
      toast.success('Equipment updated');
    } catch (error) {
      toast.error('Failed to update equipment');
    } finally {
      setSaving(false);
    }
  };

  const handleAddHistorical = async () => {
    if (!historicalForm.description || !historicalForm.income) {
      toast.error('Please fill in description and income');
      return;
    }
    
    setSaving(true);
    try {
      const entry = {
        description: historicalForm.description,
        date: historicalForm.date,
        days: parseFloat(historicalForm.days) || 0,
        income: parseFloat(historicalForm.income) || 0
      };
      
      if (editingEntryId) {
        // Update existing entry
        await updateHistoricalIncome(equipmentId, editingEntryId, entry);
        toast.success('Historical income updated');
      } else {
        // Add new entry
        await addHistoricalIncome(equipmentId, entry);
        toast.success('Historical income added');
      }
      
      await loadEquipment();
      closeHistoricalDialog();
    } catch (error) {
      toast.error(editingEntryId ? 'Failed to update historical income' : 'Failed to add historical income');
    } finally {
      setSaving(false);
    }
  };

  const openEditHistorical = (entry) => {
    setEditingEntryId(entry.id);
    setHistoricalForm({
      description: entry.job_name || '',
      date: entry.date || new Date().toISOString().split('T')[0],
      days: entry.days?.toString() || '',
      income: entry.income?.toString() || ''
    });
    setShowHistoricalDialog(true);
  };

  const closeHistoricalDialog = () => {
    setShowHistoricalDialog(false);
    setEditingEntryId(null);
    setHistoricalForm({
      description: '',
      date: new Date().toISOString().split('T')[0],
      days: '',
      income: ''
    });
  };

  // CSV Import functions
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleDownloadTemplate = () => {
    const rows = [
      ['description', 'date', 'days', 'income'],
      ['Corporate video shoot', '2024-01-15', '3', '1500.00'],
      ['Wedding - Smith family', '2024-02-20', '2', '1000.00'],
      ['Commercial photoshoot', '2024-03-10', '1', '500.00'],
    ];
    
    const csvContent = rows.map(row => 
      row.map(cell => {
        if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'historical_income_template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImportFile(file);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        toast.error('CSV file must have headers and at least one data row');
        setImportFile(null);
        return;
      }
      
      const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase());
      const requiredHeaders = ['description', 'income'];
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
      
      if (missingHeaders.length > 0) {
        toast.error(`Missing required columns: ${missingHeaders.join(', ')}`);
        setImportFile(null);
        return;
      }
      
      const preview = [];
      for (let i = 1; i < Math.min(lines.length, 6); i++) {
        const values = parseCSVLine(lines[i]);
        const item = {};
        headers.forEach((header, idx) => {
          item[header] = values[idx] || '';
        });
        if (item.description) {
          preview.push(item);
        }
      }
      
      setImportPreview(preview);
    };
    reader.readAsText(file);
  };

  const handleImportCSV = async () => {
    if (!importFile) return;
    
    setImporting(true);
    const toastId = toast.loading('Importing historical income...');
    
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase());
        
        const entries = [];
        
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          const item = {};
          headers.forEach((header, idx) => {
            item[header] = values[idx] || '';
          });
          
          if (!item.description) continue;
          
          entries.push({
            description: item.description,
            date: item.date || null,
            days: parseFloat(item.days) || 0,
            income: parseFloat(item.income) || 0
          });
        }
        
        if (entries.length === 0) {
          toast.error('No valid entries found in CSV', { id: toastId });
          setImporting(false);
          return;
        }
        
        try {
          const result = await bulkImportHistoricalIncome(equipmentId, entries);
          await loadEquipment();
          setShowImportDialog(false);
          setImportFile(null);
          setImportPreview([]);
          toast.success(`Imported ${result.entries_imported} historical income entries`, { id: toastId });
        } catch (err) {
          toast.error('Failed to import historical income', { id: toastId });
        }
        
        setImporting(false);
      };
      reader.readAsText(importFile);
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import CSV', { id: toastId });
      setImporting(false);
    }
  };

  const handleRemoveEntry = async () => {
    if (!deleteEntryId) return;
    
    try {
      await removeHistoricalIncome(equipmentId, deleteEntryId);
      await loadEquipment();
      toast.success('Entry removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove entry');
    } finally {
      setDeleteEntryId(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!equipment) {
    return (
      <Layout>
        <div className="text-center py-24">
          <p className="text-muted-foreground">Equipment not found</p>
          <Button variant="link" onClick={() => navigate('/equipment')}>
            Back to Equipment
          </Button>
        </div>
      </Layout>
    );
  }

  // ROI calculation including disposal data
  const purchasePrice = equipment.purchase_price || 0;
  const rentalIncome = equipment.total_income || 0;
  const salePrice = equipment.sale_price || 0;
  const totalReturn = rentalIncome + salePrice;
  const netProfit = totalReturn - purchasePrice;
  const roiValue = purchasePrice > 0 ? (netProfit / purchasePrice) * 100 : 0;
  const roiPositive = roiValue >= 0;
  const usageHistory = equipment.usage_history || [];
  const jobEntries = usageHistory.filter(e => e.type === 'job' || !e.type);
  const historicalEntries = usageHistory.filter(e => e.type === 'historical');
  // Newest first, for the table and the phone list
  const sortedHistory = [...usageHistory].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <Layout>
      <div className="space-y-8 animate-in" data-testid="equipment-detail-page">
        {/* Header */}
        <div className="sm:flex sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            {/* Phone: back and title in the sticky bar instead */}
            <PhoneTitle title={equipment.name} onBack={() => navigate('/equipment')} backLabel="Back to Equipment" />
            <div className="title-row gap-3 max-sm:hidden">
              <BackButton onClick={() => navigate('/equipment')} label="Back to Equipment" />
              <h1 className="record-title">{equipment.name}</h1>
              <div className="hidden sm:block w-px h-6 bg-line" />
              <div className="hidden sm:flex items-center gap-2">
                <CategoryBadge category={equipment.category} />
                {equipment.is_active ? (
                  <Badge variant="secondary" className="bg-moss-soft text-moss">Active</Badge>
                ) : (
                  <Badge variant="secondary">Inactive</Badge>
                )}
              </div>
            </div>
            {/* Phone: the badges and Edit share one row under the title */}
            <div className="flex sm:hidden items-center gap-2">
              <CategoryBadge category={equipment.category} />
              {equipment.is_active ? (
                <Badge variant="secondary" className="bg-moss-soft text-moss">Active</Badge>
              ) : (
                <Badge variant="secondary">Inactive</Badge>
              )}
              {!isEditing && (
                <Button size="sm" variant="outline" className="ml-auto" onClick={() => setIsEditing(true)}>Edit</Button>
              )}
            </div>
          </div>
          <div className={`items-center gap-2 max-sm:mt-4 ${isEditing ? 'flex' : 'hidden sm:flex'}`}>
            {isEditing ? (
              <>
                <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save changes
                </Button>
              </>
            ) : (
              <Button onClick={() => setIsEditing(true)}>Edit equipment</Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Equipment Details Card */}
            <Card>
              <CardHeader>
                <CardTitle>Equipment details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Name</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="category">Category</Label>
                        <Select
                          value={formData.category}
                          onValueChange={(value) => setFormData({ ...formData, category: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map((cat) => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="description">Description (package contents)</Label>
                        <Textarea
                          id="description"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          placeholder="List what's included in this equipment package..."
                          className="h-24"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="serial_numbers">Serial numbers (hidden)</Label>
                        <Textarea
                          id="serial_numbers"
                          value={formData.serial_numbers}
                          onChange={(e) => setFormData({ ...formData, serial_numbers: e.target.value })}
                          placeholder="Internal tracking only, not shown on estimates..."
                          className="h-24"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="purchase_price">Purchase price ({regionConfig.currency.symbol})</Label>
                        <Input
                          id="purchase_price"
                          type="number"
                          step="0.01"
                          value={formData.purchase_price}
                          onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="purchase_date">Purchase date</Label>
                        <DatePicker
                          value={formData.purchase_date}
                          onChange={(value) => setFormData({ ...formData, purchase_date: value })}
                          placeholder="Select date"
                          region={regionConfig.code}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="daily_rate">Daily rate ({regionConfig.currency.symbol})</Label>
                        <Input
                          id="daily_rate"
                          type="number"
                          step="0.01"
                          value={formData.daily_rate}
                          onChange={(e) => setFormData({ ...formData, daily_rate: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="weekly_rate">Weekly rate ({regionConfig.currency.symbol}) <span className="text-muted-foreground font-normal text-xs">optional</span></Label>
                        <Input
                          id="weekly_rate"
                          type="number"
                          step="0.01"
                          value={formData.weekly_rate}
                          onChange={(e) => setFormData({ ...formData, weekly_rate: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id="is_active"
                        checked={formData.is_active}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                      />
                      <Label htmlFor="is_active">Active (available for jobs)</Label>
                    </div>
                    
                    {/* Disposal fields - show if archived OR if active switch is off */}
                    {(equipment.archived || !formData.is_active) && (
                      <div className="border-t border-line pt-4 mt-4">
                        <Label className="text-muted-foreground mb-3 block">Disposal information</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="sale_price">Sale price ({regionConfig.currency.symbol})</Label>
                            <Input
                              id="sale_price"
                              type="number"
                              step="0.01"
                              value={formData.sale_price}
                              onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                              placeholder="0.00"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="disposal_date">Disposal date</Label>
                            <DatePicker
                              value={formData.disposal_date}
                              onChange={(value) => setFormData({ ...formData, disposal_date: value })}
                              placeholder="Select date"
                              region={regionConfig.code}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="buyer_notes">Buyer / notes</Label>
                            <Input
                              id="buyer_notes"
                              value={formData.buyer_notes}
                              onChange={(e) => setFormData({ ...formData, buyer_notes: e.target.value })}
                              placeholder="e.g., Sold to..."
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {equipment.description && (
                      <div>
                        <Label className="text-muted-foreground">Package contents</Label>
                        <p className="mt-1 whitespace-pre-wrap">{equipment.description}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4 pt-2 sm:w-fit">
                      <div>
                        <Label className="text-muted-foreground text-sm">Purchase price</Label>
                        <p className="text-lg tabular-nums">{formatCurrency(equipment.purchase_price || 0)}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-sm">Purchase date</Label>
                        <p className="text-lg tabular-nums">{equipment.purchase_date ? formatDate(equipment.purchase_date) : '-'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-sm">Daily rate</Label>
                        <p className="text-lg tabular-nums">{formatCurrency(equipment.daily_rate || 0)}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-sm">Weekly rate</Label>
                        <p className="text-lg tabular-nums">{equipment.weekly_rate ? formatCurrency(equipment.weekly_rate) : <span className="text-muted-foreground text-base">—</span>}</p>
                      </div>
                    </div>
                    
                    {/* Disposal Info - only show if archived */}
                    {equipment.archived && equipment.sale_price !== undefined && equipment.sale_price !== null && (
                      <div className="border-t border-line pt-4 mt-4">
                        <Label className="text-muted-foreground mb-3 block">Disposal information</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4 sm:w-fit">
                          <div>
                            <Label className="text-muted-foreground text-sm">Sale price</Label>
                            <p className="text-lg tabular-nums text-moss">{formatCurrency(equipment.sale_price)}</p>
                          </div>
                          <div>
                            <Label className="text-muted-foreground text-sm">Disposal date</Label>
                            <p className="text-lg tabular-nums">{equipment.disposal_date ? formatDate(equipment.disposal_date) : '-'}</p>
                          </div>
                          <div>
                            <Label className="text-muted-foreground text-sm">Net loss/gain</Label>
                            <p className={`text-lg tabular-nums ${(equipment.sale_price - (equipment.purchase_price || 0)) >= 0 ? 'text-moss' : 'text-brick'}`}>
                              {signPrefix(equipment.sale_price - (equipment.purchase_price || 0))}{formatCurrency(equipment.sale_price - (equipment.purchase_price || 0))}
                            </p>
                          </div>
                        </div>
                        {equipment.buyer_notes && (
                          <div className="mt-3">
                            <Label className="text-muted-foreground text-sm">Buyer / notes</Label>
                            <p className="mt-1 text-sm">{equipment.buyer_notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Income History Card */}
            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle>Income history</CardTitle>
                  <CardDescription>All jobs and historical entries contributing to ROI</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setShowImportDialog(true)}>
                    <Upload className="w-4 h-4" /> Import CSV
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditingEntryId(null); setShowHistoricalDialog(true); }}>
                    <Plus className="w-4 h-4" /> Add historical
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {usageHistory.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No income recorded yet</p>
                    <p className="text-sm mt-1">Complete jobs or add historical income to track ROI</p>
                  </div>
                ) : (
                  <>
                  {/* Phone: a list. A historical entry opens to edit; a job entry opens the job */}
                  <PhoneList className="-mx-2 bg-transparent rounded-none" data-testid="income-history-phone-list">
                    {sortedHistory.map((entry) => (
                      <PhoneListRow
                        key={entry.id || entry.job_id}
                        className="px-2"
                        onClick={entry.type === 'historical' ? () => openEditHistorical(entry) : undefined}
                        to={entry.type !== 'historical' && entry.job_id ? `/jobs/${entry.job_id}` : undefined}
                        title={entry.job_name}
                        detail={[entry.date ? formatDate(entry.date) : null,
                          entryTime(entry),
                          entry.type === 'historical' ? 'Historical' : entry.client_name || 'Job',
                        ].filter(Boolean).join(' · ')}
                        trailing={<span className="font-medium">{formatCurrency(entry.income || 0)}</span>}
                      />
                    ))}
                  </PhoneList>
                  <Table containerClassName="max-sm:hidden">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Time</TableHead>
                        <TableHead className="text-right">Income</TableHead>
                        <TableHead className="w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedHistory
                        .map((entry) => (
                          <TableRow key={entry.id || entry.job_id}>
                            <TableCell className="text-muted-foreground">
                              {entry.date ? formatDate(entry.date) : '-'}
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{entry.job_name}</p>
                                {entry.client_name && (
                                  <p className="text-sm text-muted-foreground">{entry.client_name}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={entry.type === 'historical' ? 'secondary' : 'outline'}>
                                {entry.type === 'historical' ? 'Historical' : 'Job'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">{entryTime(entry)}</TableCell>
                            <TableCell className="text-right tabular-nums font-medium">
                              {formatCurrency(entry.income || 0)}
                            </TableCell>
                            <TableCell>
                              {entry.type === 'historical' ? (
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => openEditHistorical(entry)}
                                    title="Edit entry"
                                  >
                                    <Edit className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => setDeleteEntryId(entry.id)}
                                    title="Delete entry"
                                  >
                                    <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                                  </Button>
                                </div>
                              ) : entry.job_id ? (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  asChild
                                >
                                  <Link to={`/jobs/${entry.job_id}`}>
                                    <ExternalLink className="w-4 h-4 text-muted-foreground" />
                                  </Link>
                                </Button>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - ROI Summary. First on a phone, as it is what the page is
              for; but not while editing, when the form should come first */}
          <div className={`space-y-4 ${isEditing ? '' : 'max-lg:order-first'}`}>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  {roiPositive ? (
                    <TrendingUp className="w-[18px] h-[18px] text-moss" />
                  ) : (
                    <TrendingDown className="w-[18px] h-[18px] text-brick" />
                  )}
                  ROI summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="py-4">
                  <p className={`text-5xl font-light tracking-tight tabular-nums ${roiPositive ? 'text-moss' : 'text-brick'}`}>
                    {roiValue >= 0 ? '+' : ''}{roiValue.toFixed(1)}%
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">Return on investment</p>
                </div>
                
                <div className="border-t border-line pt-4 space-y-3">
                  {/* Investment Section */}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Purchase price</span>
                    <span className="tabular-nums">
                      {formatCurrency(equipment.purchase_price || 0)}
                    </span>
                  </div>
                  
                  {/* Show disposal data if available */}
                  {equipment.sale_price !== null && equipment.sale_price !== undefined && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Sale price</span>
                        <span className="tabular-nums text-moss">
                          {formatCurrency(equipment.sale_price)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm pb-2 border-b border-line">
                        <span className="text-muted-foreground">Net investment</span>
                        <span className="tabular-nums">
                          {formatCurrency((equipment.purchase_price || 0) - (equipment.sale_price || 0))}
                        </span>
                      </div>
                    </>
                  )}
                  
                  {/* Income Section */}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Rental income</span>
                    <span className="tabular-nums text-moss">
                      {formatCurrency(equipment.total_income || 0)}
                    </span>
                  </div>
                  
                  {/* Total Return if disposed */}
                  {equipment.sale_price !== null && equipment.sale_price !== undefined && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total return</span>
                      <span className="tabular-nums text-moss">
                        {formatCurrency((equipment.total_income || 0) + (equipment.sale_price || 0))}
                      </span>
                    </div>
                  )}
                  
                  {/* Net Profit/Loss */}
                  <div className="flex justify-between text-sm border-t border-line pt-3 mt-2">
                    <span className="text-muted-foreground">Net profit/loss</span>
                    <span className={`tabular-nums ${
                      ((equipment.total_income || 0) + (equipment.sale_price || 0) - (equipment.purchase_price || 0)) >= 0 
                        ? 'text-moss' 
                        : 'text-brick'
                    }`}>
                      {signPrefix((equipment.total_income || 0) + (equipment.sale_price || 0) - (equipment.purchase_price || 0))}{formatCurrency(
                        (equipment.total_income || 0) + (equipment.sale_price || 0) - (equipment.purchase_price || 0)
                      )}
                    </span>
                  </div>
                </div>
                
                <div className="border-t border-line pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total days rented</span>
                    <span className="tabular-nums">{equipment.total_days || 0} days</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Job entries</span>
                    <span className="tabular-nums">{jobEntries.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Historical entries</span>
                    <span className="tabular-nums">{historicalEntries.length}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Break-even Analysis */}
            {equipment.purchase_price > 0 && equipment.daily_rate > 0 && (() => {
              // Calculate break-even including disposal/sale price
              const totalBreakEvenDays = Math.ceil(equipment.purchase_price / equipment.daily_rate);
              const totalRecovered = (equipment.total_income || 0) + (equipment.sale_price || 0);
              const remainingAmount = Math.max(0, (equipment.purchase_price || 0) - totalRecovered);
              const daysRemaining = Math.ceil(remainingAmount / equipment.daily_rate);
              const hasReachedBreakEven = totalRecovered >= equipment.purchase_price;
              const progressPercent = Math.min(100, (totalRecovered / equipment.purchase_price) * 100);
              const isDisposed = equipment.sale_price !== null && equipment.sale_price !== undefined;
              
              return (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-xl">Break-even analysis</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Days to break even</span>
                      <span className="tabular-nums">
                        {totalBreakEvenDays} days
                      </span>
                    </div>
                    {!isDisposed ? (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Days remaining</span>
                        <span className={`tabular-nums ${hasReachedBreakEven ? 'text-moss' : ''}`}>
                          {hasReachedBreakEven ? '0 days (Reached!)' : `${daysRemaining} days`}
                        </span>
                      </div>
                    ) : (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Status</span>
                        <span className={hasReachedBreakEven ? 'text-moss' : 'text-brick'}>
                          {hasReachedBreakEven ? 'Break-even reached' : 'Disposed (loss)'}
                        </span>
                      </div>
                    )}
                    <div className="w-full bg-mist rounded-pill h-2 mt-2">
                      <div
                        className={`h-2 rounded-pill transition-all ${hasReachedBreakEven ? 'bg-moss' : 'bg-primary'}`}
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                    {!hasReachedBreakEven && (
                      <p className="text-xs text-muted-foreground">
                        {isDisposed 
                          ? `${formatCurrency(remainingAmount)} loss after disposal`
                          : `${formatCurrency(remainingAmount)} more income needed at ${formatCurrency(equipment.daily_rate)}/day`
                        }
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Add/Edit Historical Income Dialog */}
      <Dialog open={showHistoricalDialog} onOpenChange={closeHistoricalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingEntryId ? 'Edit historical income' : 'Add historical income'}</DialogTitle>
            <DialogDescription>
              {editingEntryId ? 'Update this historical income entry' : 'Record income earned before using this app'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="hist-description">Description *</Label>
              <Input
                id="hist-description"
                value={historicalForm.description}
                onChange={(e) => setHistoricalForm({ ...historicalForm, description: e.target.value })}
                placeholder="e.g., Wedding shoot for John & Jane"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hist-date">Date</Label>
                <DatePicker
                  value={historicalForm.date}
                  onChange={(value) => setHistoricalForm({ ...historicalForm, date: value })}
                  placeholder="Select date"
                  region={regionConfig.code}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hist-days">Days</Label>
                <Input
                  id="hist-days"
                  type="number"
                  step="0.5"
                  value={historicalForm.days}
                  onChange={(e) => setHistoricalForm({ ...historicalForm, days: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="hist-income">Income ({regionConfig.currency.symbol}) *</Label>
              <Input
                id="hist-income"
                type="number"
                step="0.01"
                value={historicalForm.income}
                onChange={(e) => setHistoricalForm({ ...historicalForm, income: e.target.value })}
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeHistoricalDialog}>Cancel</Button>
            <Button onClick={handleAddHistorical} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {editingEntryId ? 'Save changes' : 'Add entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(open) => {
        setShowImportDialog(open);
        if (!open) {
          setImportFile(null);
          setImportPreview([]);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import historical income from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file to bulk import historical income entries.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Download Template */}
            <div className="flex items-center justify-between p-3 bg-mist rounded-cards">
              <div>
                <p className="text-sm">CSV template</p>
                <p className="text-xs text-muted-foreground">Download template with example data</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                <Download className="w-4 h-4" /> Download
              </Button>
            </div>
            
            {/* Format info */}
            <div className="text-xs text-muted-foreground space-y-1 p-3 bg-mist/50 rounded-cards">
              <p><strong>Required columns:</strong> description, income</p>
              <p><strong>Optional:</strong> date (YYYY-MM-DD), days</p>
            </div>
            
            {/* File Upload */}
            <FileDropzone accept=".csv" onSelect={handleFileSelect} title="Choose a CSV file" />
            
            {/* Preview */}
            {importPreview.length > 0 && (
              <div className="space-y-2">
                <Label>Preview (first {importPreview.length} entries)</Label>
                <div className="max-h-[200px] overflow-auto border border-line rounded-cards">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Description</TableHead>
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs text-right">Days</TableHead>
                        <TableHead className="text-xs text-right">Income</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importPreview.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-sm py-2">{item.description}</TableCell>
                          <TableCell className="text-sm py-2">{item.date || '-'}</TableCell>
                          <TableCell className="text-sm py-2 text-right">{item.days || 0}</TableCell>
                          <TableCell className="text-sm py-2 text-right tabular-nums">
                            {formatCurrency(parseFloat(item.income) || 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            
            {/* Import Button */}
            <Button 
              onClick={handleImportCSV} 
              disabled={!importFile || importing}
              className="w-full"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Import historical income
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteEntryId} onOpenChange={() => setDeleteEntryId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the historical income entry and update the ROI calculations. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveEntry} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
